import { PropertyStatusBadge } from '@/components/property/property-status-badge';
import { DataPanel, type DataPanelItem } from '@/components/shared/data-panel';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { DashboardPageLayout } from '@/layouts/page/dashboard-page-layout';
import { formatAddress } from '@/lib/address-utils';
import { formatCalendarDate } from '@/lib/date-utils';
import { invariant } from '@/lib/invariant';
import { getPropertyStrategyName, getPropertyTypeName } from '@/lib/property-utils';
import { useReturnTo, useReturnToState } from '@/lib/use-return-to';
import { useAuthService } from '@/providers/auth-service-provider';
import { useOrganizationService } from '@/providers/organization-service-provider';
import { PATHS } from '@/routes/paths';
import { usePropertiesGetProperty } from '@portfolio-manager/query';
import type { ApiError, PropertyResponseDto } from '@portfolio-manager/sdk';
import { type UseQueryResult } from '@tanstack/react-query';
import React from 'react';
import { Link, Outlet, useLocation, useParams } from 'react-router';

interface PropertyTab {
  value: string;
  label: string;
  to: (propertyId: string) => string;
}

const TABS: PropertyTab[] = [
  {
    value: 'units',
    label: 'Units',
    to: PATHS.propertyUnits,
  },
  {
    value: 'notes',
    label: 'Notes',
    to: PATHS.propertyNotes,
  },
];

const getTitle = (query: UseQueryResult<PropertyResponseDto, ApiError>): React.ReactNode => {
  if (query.isPending) {
    return <Skeleton className="w-60 h-6" />;
  }
  if (query.isSuccess) {
    return `${query.data.nickname}`;
  }
  return 'Property';
};

const getDescription = (query: UseQueryResult<PropertyResponseDto, ApiError>): React.ReactNode => {
  if (query.isPending) {
    return <Skeleton className="w-100 h-4" />;
  }
  if (query.isSuccess) {
    return `Manage ${query.data.nickname}.`;
  }
  return 'Manage the property.';
};

const items: DataPanelItem<PropertyResponseDto>[] = [
  {
    key: 'nickname',
    label: 'Name',
    value: (p) => p.nickname,
  },
  {
    key: 'status',
    label: 'Status',
    value: (p) => <PropertyStatusBadge property={p} />,
  },
  {
    key: 'address',
    label: 'Address',
    value: (p) => formatAddress(p),
  },
  {
    key: 'county',
    label: 'County',
    value: (p) => p.county,
  },
  {
    key: 'apn',
    label: 'Parcel number',
    value: (p) => p.apn,
  },
  {
    key: 'propertyType',
    label: 'Property type',
    value: (p) => getPropertyTypeName(p.propertyType),
  },
  {
    key: 'strategy',
    label: 'Strategy',
    value: (p) => getPropertyStrategyName(p.strategy),
  },
  {
    key: 'lotSqft',
    label: 'Lot size',
    value: (p) => (p.lotSqft === null ? null : `${p.lotSqft.toLocaleString()} sqft`),
  },
  {
    key: 'yearBuilt',
    label: 'Year built',
    value: (p) => (p.yearBuilt === null ? null : `${p.yearBuilt}`),
  },
  {
    key: 'acquisitionDate',
    label: 'Acquired',
    value: (p) => formatCalendarDate(p.acquisitionDate, ''),
  },
  {
    key: 'dispositionDate',
    label: 'Disposed',
    value: (p) => formatCalendarDate(p.dispositionDate, ''),
  },
];

export function PropertyTabbedLayout(): React.ReactElement {
  const authService = useAuthService();
  const userId = authService.getUserId();
  invariant(userId);

  const organizationService = useOrganizationService();
  const organizationId = organizationService.organizationId;
  invariant(organizationId);

  const { propertyId } = useParams();
  invariant(propertyId);

  const returnTo = useReturnTo(PATHS.properties);

  const returnToState = useReturnToState();

  const queryProperty = usePropertiesGetProperty({ organizationId, propertyId });

  const { pathname } = useLocation();

  const activeTab = TABS.find((tab) => pathname.startsWith(tab.to(propertyId))) ?? TABS[0];

  return (
    <DashboardPageLayout
      title={getTitle(queryProperty)}
      description={getDescription(queryProperty)}
      backLink={returnTo}
      actions={
        <Button asChild>
          <Link to={PATHS.updateProperty(propertyId)} state={returnToState}>
            Update property
          </Link>
        </Button>
      }
    >
      <div className="flex flex-col gap-4">
        <DataPanel
          className="border-b"
          items={items}
          record={queryProperty.data}
          isPending={queryProperty.isPending}
          error={queryProperty.error}
          showMoreLabel="More details"
          showLessLabel="Less details"
          maxItems={3}
        />
        <Tabs value={activeTab.value} className="">
          <TabsList variant="line" className="mx-4">
            {TABS.map((tab) => (
              <TabsTrigger key={tab.value} value={tab.value} className="min-w-24 px-4" asChild>
                <Link to={tab.to(propertyId)}>{tab.label}</Link>
              </TabsTrigger>
            ))}
          </TabsList>
          <TabsContent value={activeTab.value}>
            <Outlet />
          </TabsContent>
        </Tabs>
      </div>
    </DashboardPageLayout>
  );
}
